import * as Sentry from '@sentry/react'

/**
 * Sentry telemetry for the 3D Secure (Verifone / Cardinal) card payment flow.
 *
 * Every event is tagged with `flow: 3ds` and `3ds.step` so the flow can be filtered and
 * graphed in Sentry (e.g. `flow:3ds 3ds.outcome:success` vs `flow:3ds 3ds.outcome:error`).
 *
 * PII / sensitive data rules: never pass card data, the encrypted card, JWTs, CAVV,
 * ACS payloads, names, emails or addresses into this module. Errors are re-created from a
 * sanitized message so that e.g. graphql-request `ClientError` (whose `message` embeds the
 * full request variables, including billing information) never reaches Sentry.
 */

export const THREE_DS_FLOW = '3ds'
export const THREE_DS_CONTEXT_KEY = 'threeDSecure'
const BREADCRUMB_CATEGORY = 'payment.3ds'
const UNKNOWN_ERROR = 'Unknown error'
const MAX_TAG_LENGTH = 200

export type ThreeDSStep =
  | 'start'
  | 'token_exchange'
  | 'card_encryption'
  | 'card_token'
  | 'cardinal_setup'
  | 'billing_info'
  | 'lookup'
  | 'challenge'
  | 'challenge_validation'
  | 'authentication_result'
  | 'recurring_payment'
  | 'confirm_payment'

/** `declined` = expected business outcome (issuer/cardholder said no), `error` = something broke. */
export type ThreeDSOutcome = 'success' | 'declined' | 'error'

export type ThreeDSContext = {
  challengeRequired?: boolean
  lookupParesStatus?: string
  enrolledStatus?: string
  threeDSecureVersion?: string
  electronicCommerceIndicator?: string
  transactionId?: string
  actionCode?: string
  errorNumber?: number
  errorDescription?: string
  challengeParesStatus?: string
  signatureVerification?: string
  hasThreeDAuthentication?: boolean
  confirmPaymentCode?: string
  durationMs?: number
}

export class ThreeDSecureError extends Error {
  readonly step: ThreeDSStep
  readonly context: ThreeDSContext
  readonly outcome: Exclude<ThreeDSOutcome, 'success'>

  constructor(
    step: ThreeDSStep,
    message: string,
    context: ThreeDSContext = {},
    outcome: Exclude<ThreeDSOutcome, 'success'> = 'error'
  ) {
    super(message)
    // Required because the project compiles to ES5, where extending Error breaks the prototype chain
    Object.setPrototypeOf(this, ThreeDSecureError.prototype)
    this.name = 'ThreeDSecureError'
    this.step = step
    this.context = context
    this.outcome = outcome
  }
}

export const isThreeDSecureError = (error: unknown): error is ThreeDSecureError => error instanceof ThreeDSecureError

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null

/**
 * Extract a message that is safe to send to Sentry.
 * graphql-request `ClientError` puts `JSON.stringify({ response, request })` into `message`,
 * so for anything carrying a `response` we only use the GraphQL error message / status.
 */
export const getSafeErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') {
    return error || UNKNOWN_ERROR
  }
  if (!isRecord(error)) {
    return UNKNOWN_ERROR
  }
  const response = error.response
  if (isRecord(response)) {
    const errors = response.errors
    const firstError: unknown = Array.isArray(errors) ? errors[0] : undefined
    if (isRecord(firstError) && typeof firstError.message === 'string') {
      return firstError.message
    }
    return `GraphQL Error (Code: ${String(response.status)})`
  }
  if (typeof error.message === 'string' && error.message) {
    return error.message
  }
  return UNKNOWN_ERROR
}

const truncate = (value: string): string => (value.length > MAX_TAG_LENGTH ? value.slice(0, MAX_TAG_LENGTH) : value)

type ContextValue = string | number | boolean

const isContextValue = (value: unknown): value is ContextValue =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'

/** Remove undefined/null values so Sentry contexts and breadcrumbs stay readable. */
export const compactContext = (context: ThreeDSContext): Record<string, ContextValue> =>
  Object.fromEntries(
    Object.entries(context).filter((entry): entry is [string, ContextValue] => isContextValue(entry[1]))
  )

export const buildThreeDSTags = (
  step: ThreeDSStep,
  outcome: ThreeDSOutcome | undefined,
  context: ThreeDSContext
): Record<string, string> => {
  const tags: Record<string, string | undefined> = {
    flow: THREE_DS_FLOW,
    '3ds.step': step,
    '3ds.outcome': outcome,
    '3ds.challenge': context.challengeRequired === undefined ? undefined : String(context.challengeRequired),
    '3ds.lookup_pares_status': context.lookupParesStatus,
    '3ds.challenge_pares_status': context.challengeParesStatus,
    '3ds.action_code': context.actionCode,
    '3ds.enrolled': context.enrolledStatus,
    '3ds.version': context.threeDSecureVersion,
    '3ds.confirm_code': context.confirmPaymentCode,
  }
  return Object.fromEntries(
    Object.entries(tags)
      .filter((entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1] !== '')
      .map(([key, value]) => [key, truncate(value)])
  )
}

/** Reason used to group failures in Sentry, most specific first. */
export const getFailureReason = (context: ThreeDSContext, message: string): string =>
  context.actionCode ?? context.challengeParesStatus ?? context.lookupParesStatus ?? truncate(message)

export const addThreeDSBreadcrumb = (step: ThreeDSStep, message: string, context: ThreeDSContext = {}) => {
  Sentry.addBreadcrumb({
    category: BREADCRUMB_CATEGORY,
    level: 'info',
    message: `[${step}] ${message}`,
    data: compactContext(context),
  })
}

/**
 * Capture a failure in the 3DS flow. `error` may be anything; only a sanitized message is sent.
 * If `error` is a ThreeDSecureError its step/context/outcome are merged in.
 */
export const captureThreeDSFailure = (
  step: ThreeDSStep,
  error: unknown,
  context: ThreeDSContext = {},
  outcome: Exclude<ThreeDSOutcome, 'success'> = 'error'
) => {
  const resolvedStep = isThreeDSecureError(error) ? error.step : step
  const resolvedOutcome = isThreeDSecureError(error) ? error.outcome : outcome
  const resolvedContext: ThreeDSContext = isThreeDSecureError(error) ? { ...context, ...error.context } : context
  const message = getSafeErrorMessage(error)
  const sentryError = new ThreeDSecureError(resolvedStep, message, resolvedContext, resolvedOutcome)

  Sentry.captureException(sentryError, {
    level: resolvedOutcome === 'declined' ? 'warning' : 'error',
    tags: buildThreeDSTags(resolvedStep, resolvedOutcome, resolvedContext),
    contexts: { [THREE_DS_CONTEXT_KEY]: compactContext(resolvedContext) },
    fingerprint: ['3ds-failure', resolvedStep, getFailureReason(resolvedContext, message)],
  })
}

/**
 * Context of the last successful card tokenization, so the final "payment confirmed" event
 * (sent from Checkout `handleCheckout`, outside the 3DS hook) can be split by challenge/frictionless.
 */
let lastTokenizationContext: ThreeDSContext = {}

/** Stage 1 of 2: 3DS authentication done and Verifone card token (recurring payment) created. */
export const captureThreeDSSuccess = (context: ThreeDSContext) => {
  lastTokenizationContext = context
  const variant = context.challengeRequired ? 'challenge' : 'frictionless'
  Sentry.captureMessage(`3DS card tokenized (${variant})`, {
    level: 'info',
    tags: buildThreeDSTags('recurring_payment', 'success', context),
    contexts: { [THREE_DS_CONTEXT_KEY]: compactContext(context) },
    fingerprint: ['3ds-success', 'recurring_payment', variant],
  })
}

/** Stage 2 of 2: policyhub checkout (confirmPayment) accepted the card token. This is the end-to-end success event. */
export const captureThreeDSPaymentConfirmed = () => {
  const context = lastTokenizationContext
  const variant = context.challengeRequired ? 'challenge' : 'frictionless'
  Sentry.captureMessage(`3DS card payment confirmed (${variant})`, {
    level: 'info',
    tags: buildThreeDSTags('confirm_payment', 'success', context),
    contexts: { [THREE_DS_CONTEXT_KEY]: compactContext(context) },
    fingerprint: ['3ds-success', 'confirm_payment', variant],
  })
}

/** Failure of policyhub checkout (confirmPayment) for a card payment. Uses the last tokenization context for correlation. */
export const captureThreeDSConfirmPaymentFailure = (error: unknown, outcome: Exclude<ThreeDSOutcome, 'success'>) => {
  captureThreeDSFailure('confirm_payment', error, lastTokenizationContext, outcome)
}

/**
 * Non-sensitive diagnostics from the object `setupPaymentValidation` rejects with
 * (`{ validated, data, jwt, message }`). The jwt is intentionally ignored.
 */
export const getCardinalRejectionContext = (error: unknown): ThreeDSContext => {
  if (!isRecord(error) || !isRecord(error.data)) {
    return {}
  }
  const data = error.data
  const payment = isRecord(data.Payment) ? data.Payment : undefined
  const extendedData = payment && isRecord(payment.ExtendedData) ? payment.ExtendedData : undefined
  const readString = (value: unknown) => (typeof value === 'string' && value !== '' ? value : undefined)
  return {
    actionCode: readString(data.ActionCode),
    errorNumber: typeof data.ErrorNumber === 'number' ? data.ErrorNumber : undefined,
    errorDescription: readString(data.ErrorDescription),
    challengeParesStatus: readString(extendedData?.PAResStatus),
  }
}
